import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Checkbox,
  Collapse,
  Divider,
  Paper,
  Button,
  Modal,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { PokemonV2 } from '../../pokemon/pokemon';
import type { PokemonOption } from '../../types/pokemon';
import type { ItemOption } from '../../types/item';
import type { AttackPokemonData } from '../../types/attack';
import type { otherSetting } from '../../types/other-setting';
import type { retCalc } from '../../types/damage';
import type { MoveDamageSetting } from '../../types/damage-setting';
import type {
  ComprehensiveUiFlags,
  DamageCategory,
  OpponentCard,
  OpponentSpeedSettings,
  SpeedModifiers,
} from '../../types/comprehensive';
import { createOpponentSpeedSettings } from '../../types/comprehensive';
import { Move, MOVE_DAMAGE_CLASS_STATUS, isDamagingMove } from '../../move/move';
import { getMovesByIds } from '../../lib/move-access';
import { getArceusEffectiveType } from '../../pokemon/PlateUtils';
import { calculateOffenceSpeed, calculateActualStats } from '../../lib/stat';
import { calcRealValueHPStat } from '../../lib/stat/real-value';
import { MAX_STAT_EFFORT } from '../../constants/effort';
import { OFFENCE_ITEM_ID_KODAWARISUKAFU } from '../../types/item';
import PokemonSelectInput from '../../components/PokemonSelectInput';
import AttackPokemonCardFrame from '../../components/AttackPokemonCardFrame';
import AttackModeDisplay from '../OffenceMode/AttackModeDisplay';
import AttackPokemonItemInput from '../../components/AttackPokemonItemInput';
import AttackPokemonAbilityInput from '../../components/AttackPokemonAbilityInput';
import AttackPokemonTeraSelector from '../../components/AttackPokemonTeraSelector';
import StatusAilmentSelect, { StatusAilmentKey } from '../../components/StatusAilmentSelect';
import DefencePokemonStatInputAttackMode from '../OffenceMode/DefencePokemonStatInputAttackMode';
import InputMoves from '../../LeftSideBar/InputMoves';
import DamageResultDetails from '../../components/DamageResultDetails';
import {
  DamageSettingHeaderControls,
  DamageSettingPowerSelect,
  buildDamageSettingSummaryTags,
  resolveDamageSettingVisibility,
  resolveMoveDamageSettingWithDefaults,
} from '../../components/DamageSettingControls';
import OtherSetting from '../../OtherSetting';
import { POKEMON_ID_GROUDON_PRIMAL, POKEMON_ID_KYOGRE_PRIMAL, POKEMON_ID_RAYQUAZA_MEGA } from '../../lib/pokemon-config';
import { resolveOffenceMoveType } from '../../lib/move-type';
import { POKEMON_TYPE_ELECTRIC, type PokemonType } from '../../types/type';
import { storageClient } from '../../storage';

interface ComprehensiveModeProps {
  selectedPokemon: PokemonV2 | null;
  pokemonOptions: PokemonOption[];
  opponents: OpponentCard[];
  selectedOpponentId: string | null;
  uiFlags: ComprehensiveUiFlags;
  onAddOpponent: (event: any, newValue: any) => void;
  onSelectOpponent: (opponentId: string) => void;
  onRemoveOpponent: (opponentId: string) => void;
  onCopyOpponent: (opponentId: string) => void;
  onUpdateAssumedMove: (opponentId: string, moveIndex: number, move: Move | null) => void;
  onUpdateOpponentOtherSetting: (opponentId: string, setting: otherSetting) => void;
  onUpdateOpponentSpeedSettings: (opponentId: string, settings: OpponentSpeedSettings) => void;
  onCreateDefenseProfile: (opponentId: string) => void;
  onCreateOffenseProfile: (opponentId: string) => void;
  setUiFlags: React.Dispatch<React.SetStateAction<ComprehensiveUiFlags>>;
  currentOtherSetting: otherSetting;
  onOtherSettingChange: (setting: otherSetting) => void;
  calculateAttackModeDamage: (
    attackerPokemon: PokemonV2,
    targetPokemon: AttackPokemonData,
    overrideOtherSetting?: otherSetting,
    resolveMoveSetting?: (moveId: number) => MoveDamageSetting | undefined,
  ) => string[];
  getAttackModeDamageDetail: (
    attackerPokemon: PokemonV2,
    targetPokemon: AttackPokemonData,
    overrideOtherSetting?: otherSetting,
    resolveMoveSetting?: (moveId: number) => MoveDamageSetting | undefined,
  ) => retCalc[];
  calculateDamage: (attackPokemon: AttackPokemonData, defensePokemon: PokemonV2, overrideOtherSetting?: otherSetting) => string;
  getDamageDetail: (attackPokemon: AttackPokemonData, defensePokemon: PokemonV2, overrideOtherSetting?: otherSetting) => retCalc;
  getMoveDisplayName: (move: Move, pokemonId: number) => string;
  getMoveDisplayPower: (move: Move, pokemonId: number) => number | string;
  getOffenseItems: ItemOption[];
  handleSelectAbilityForAttackPokemon: (id: string, abilityId: number) => void;
  handleSelectItemForAttackPokemon: (id: string, itemId: number) => void;
  handleSelectTerasuTypeForAttackPokemon: (id: string, terasuType: number) => void;
  handleUpdateKodaiQuarkStat: (id: string, value: 'attack' | 'special_attack') => void;
  handleUpdateAttackPokemonStats: (
    id: string,
    field: string,
    value: number,
  ) => void;
  handleUpdateAttackPokemonPersonality: (
    id: string,
    field: 'attack_personality' | 'defense_personality' | 'special_attack_personality' | 'special_defense_personality' | 'speed_personality',
    value: number,
  ) => void;
  handleUpdateAttackPokemonRank: (
    id: string,
    field: 'attackRank' | 'defenseRank' | 'specialAttackRank' | 'specialDefenseRank' | 'speedRank',
    value: number,
  ) => void;
  handleUpdateAttackPokemonStatus: (id: string, isBurned: boolean, isPoisoned: boolean, isParalyzed: boolean) => void;
}

const SECTION_IDS = {
  damageDealt: 'comprehensive-damage-dealt',
  damageTaken: 'comprehensive-damage-taken',
  speed: 'comprehensive-speed',
} as const;

const rankMultipliers = [0.25, 0.28, 0.33, 0.4, 0.5, 0.66, 1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0];

type StatusAilmentValue = StatusAilmentKey | 'none';

// 相手横断で合算対象を管理するため、相手ID+技IDで一意化する
const createDamageTakenMoveKey = (opponentId: string, moveId: number) => `${opponentId}:${moveId}`;

const parseDamageTakenMoveKey = (key: string) => {
  const [opponentId, moveIdText] = key.split(':');
  return {
    opponentId,
    moveId: Number(moveIdText),
  };
};

const clampEffortPoint = (value: number) => {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(MAX_STAT_EFFORT, Math.floor(value)));
};

const resolveOpponentPokemon = (opponent: OpponentCard | null): PokemonV2 | null => {
  if (!opponent) return null;
  return opponent.defenseProfile?.pokemon ?? opponent.offenseProfile?.pokemon ?? null;
};

const resolveOpponentProfile = (opponent: OpponentCard | null): AttackPokemonData | null => {
  if (!opponent) return null;
  return opponent.defenseProfile ?? opponent.offenseProfile ?? null;
};

const buildOpponentActualStats = (profile: AttackPokemonData | null) => {
  if (!profile) return null;
  const statInputs = profile.statInputs;
  // 個体値は入力を優先し、未指定はポケモン側(なければ31)を採用する
  const mergedPokemon: PokemonV2 = {
    ...profile.pokemon,
    individualValue: {
      ...profile.pokemon.individualValue,
      attack: (statInputs.attack_iv ?? profile.pokemon.individualValue.attack ?? 31) as 0 | 31,
      defense: (statInputs.defense_iv ?? profile.pokemon.individualValue.defense ?? 31) as 0 | 31,
      special_attack: (statInputs.special_attack_iv ?? profile.pokemon.individualValue.special_attack ?? 31) as 0 | 31,
      special_defense: (statInputs.special_defense_iv ?? profile.pokemon.individualValue.special_defense ?? 31) as 0 | 31,
      speed: (statInputs.speed_iv ?? profile.pokemon.individualValue.speed ?? 31) as 0 | 31,
    },
  };

  return calculateActualStats(mergedPokemon, statInputs);
};

const resolveSpeedRank = (profile: AttackPokemonData) => {
  // speedRank > rankCorrection > pokemon設定の順で採用する
  if (profile.speedRank !== undefined) {
    return profile.speedRank;
  }
  if (profile.rankCorrection !== undefined) {
    return profile.rankCorrection;
  }
  return profile.pokemon.rankCorrection?.speed ?? 0;
};

const buildSpeedBasePokemon = (profile: AttackPokemonData): PokemonV2 => {
  // 素早さ計算に必要な要素だけ差し替える
  return {
    ...profile.pokemon,
    effective_slider_step: {
      ...profile.pokemon.effective_slider_step,
      speed: clampEffortPoint(profile.statInputs?.speed_effort ?? 0),
    },
    personality: {
      ...profile.pokemon.personality,
      speed: profile.statInputs?.speed_personality ?? 1.0,
    },
    individualValue: {
      ...profile.pokemon.individualValue,
      speed: (profile.statInputs?.speed_iv ?? 31) as 0 | 31,
    },
  };
};

const applySpeedModifiers = (baseSpeed: number, rank: number, modifiers: SpeedModifiers) => {
  // ランク補正→アイテム→追い風→麻痺の順で適用する
  let result = baseSpeed;
  if (rank !== 0) {
    const index = Math.max(0, Math.min(12, rank + 6));
    result = Math.floor(result * rankMultipliers[index]);
  }
  if (modifiers.scarf) {
    result = Math.floor(result * 1.5);
  }
  if (modifiers.tailwind) {
    result = Math.floor(result * 2);
  }
  if (modifiers.paralysis) {
    result = Math.floor(result * 0.5);
  }
  return result;
};

const resolveSpeedResultLabel = (selfSpeed: number, opponentSpeed: number, isTrickRoom: boolean) => {
  let resultLabel = '同速';
  if (selfSpeed > opponentSpeed) {
    resultLabel = '先手';
  } else if (selfSpeed < opponentSpeed) {
    resultLabel = '後手';
  }

  if (isTrickRoom && resultLabel !== '同速') {
    resultLabel = resultLabel === '先手' ? '後手' : '先手';
  }

  return resultLabel;
};

const getDamageCategory = (detail: retCalc | null, move: Move, defenderHp: number): DamageCategory => {
  if (!detail || defenderHp <= 0) {
    return 'invalid';
  }
  if (move.damage_class_number === MOVE_DAMAGE_CLASS_STATUS) {
    return 'invalid';
  }

  let minDamage = detail.min_damage ?? 0;
  let maxDamage = detail.max_damage ?? 0;

  if (move.max_renzoku && move.max_renzoku > 1) {
    const minHits = move.min_renzoku || 2;
    const maxHits = move.max_renzoku;

    if (maxHits > minHits) {
      // 最小ヒットの乱数分布から1回あたりの最大ダメージを拾い、最大ヒット時の判定に使う
      const hitDamages = Object.keys(detail.random_table ?? {}).map((value) => Number(value));
      if (hitDamages.length > 0) {
        const perHitMax = Math.max(...hitDamages);
        maxDamage = perHitMax * maxHits;
      } else if (minHits > 0) {
        maxDamage = Math.floor((maxDamage / minHits) * maxHits);
      }
    }
  }

  if (maxDamage === 0 && minDamage === 0) {
    return 'invalid';
  }
  if (maxDamage < defenderHp) {
    return 'survive';
  }
  if (minDamage >= defenderHp) {
    return 'guaranteed';
  }
  return 'random';
};

type SectionKey = 'commonConditions' | 'damageDealt' | 'damageTaken' | 'speed';

function ComprehensiveMode({
  selectedPokemon,
  pokemonOptions,
  opponents,
  selectedOpponentId,
  uiFlags,
  onAddOpponent,
  onSelectOpponent,
  onRemoveOpponent,
  onCopyOpponent,
  onUpdateAssumedMove,
  onUpdateOpponentOtherSetting,
  onUpdateOpponentSpeedSettings,
  onCreateDefenseProfile,
  onCreateOffenseProfile,
  setUiFlags,
  currentOtherSetting,
  onOtherSettingChange,
  calculateAttackModeDamage,
  getAttackModeDamageDetail,
  calculateDamage,
  getDamageDetail,
  getMoveDisplayName,
  getMoveDisplayPower,
  getOffenseItems,
  handleSelectAbilityForAttackPokemon,
  handleSelectItemForAttackPokemon,
  handleSelectTerasuTypeForAttackPokemon,
  handleUpdateKodaiQuarkStat,
  handleUpdateAttackPokemonStats,
  handleUpdateAttackPokemonPersonality,
  handleUpdateAttackPokemonRank,
  handleUpdateAttackPokemonStatus,
}: ComprehensiveModeProps) {
  const canSelectOoame = useMemo(
    () =>
      selectedPokemon?.id === POKEMON_ID_KYOGRE_PRIMAL ||
      opponents.some((opponent) => opponent.pokemonId === POKEMON_ID_KYOGRE_PRIMAL),
    [opponents, selectedPokemon],
  );
  const canSelectOohidery = useMemo(
    () =>
      selectedPokemon?.id === POKEMON_ID_GROUDON_PRIMAL ||
      opponents.some((opponent) => opponent.pokemonId === POKEMON_ID_GROUDON_PRIMAL),
    [opponents, selectedPokemon],
  );
  const canSelectRankiryuu = useMemo(
    () =>
      selectedPokemon?.id === POKEMON_ID_RAYQUAZA_MEGA ||
      opponents.some((opponent) => opponent.pokemonId === POKEMON_ID_RAYQUAZA_MEGA),
    [opponents, selectedPokemon],
  );
  const selectedOpponent = useMemo(
    () => opponents.find((opponent) => opponent.id === selectedOpponentId) ?? null,
    [opponents, selectedOpponentId],
  );

  const normalizeOpponentSpeedSettings = useCallback((settings?: OpponentSpeedSettings): OpponentSpeedSettings => {
    const base = createOpponentSpeedSettings();
    if (!settings) {
      return base;
    }
    // 欠けている項目を初期値で補完して、UIのチェックボックスを安定させる
    return {
      self: { ...base.self, ...settings.self },
      opponent: { ...base.opponent, ...settings.opponent },
      isTrickRoom: settings.isTrickRoom ?? base.isTrickRoom,
    };
  }, []);

  const selectedSpeedSettings = useMemo(
    () => normalizeOpponentSpeedSettings(selectedOpponent?.speedSettings),
    [normalizeOpponentSpeedSettings, selectedOpponent],
  );

  const updateSelectedSpeedSettings = useCallback((settings: OpponentSpeedSettings) => {
    if (!selectedOpponent) {
      return;
    }
    onUpdateOpponentSpeedSettings(selectedOpponent.id, settings);
  }, [onUpdateOpponentSpeedSettings, selectedOpponent]);

  const selfSpeedModifiers = selectedSpeedSettings.self;
  const opponentSpeedModifiers = selectedSpeedSettings.opponent;
  const isTrickRoom = selectedSpeedSettings.isTrickRoom;

  const selectedOpponentOtherSetting = useMemo(
    // 相手固有の共通条件があれば優先し、無ければ全体設定を使う
    () => selectedOpponent?.otherSetting ?? currentOtherSetting,
    [currentOtherSetting, selectedOpponent],
  );

  const handleSaveOtherSetting = useCallback((setting: otherSetting) => {
    if (selectedOpponent) {
      onUpdateOpponentOtherSetting(selectedOpponent.id, setting);
      return;
    }
    onOtherSettingChange(setting);
  }, [onOtherSettingChange, onUpdateOpponentOtherSetting, selectedOpponent]);

  const selectedOpponentPokemon = useMemo(
    () => resolveOpponentPokemon(selectedOpponent),
    [selectedOpponent],
  );

  // 相手横断の受けるダメージ合算チェックを保持する
  const [selectedDamageTakenMoveKeys, setSelectedDamageTakenMoveKeys] = useState<string[]>([]);
  const [isDamageTakenSummaryOpen, setIsDamageTakenSummaryOpen] = useState(false);
  // 受けるダメージ（技ごと）の追加設定を保持する
  const [damageTakenMoveSettingByKey, setDamageTakenMoveSettingByKey] = useState<Record<string, MoveDamageSetting>>({});
  const [damageTakenDetailOpenByKey, setDamageTakenDetailOpenByKey] = useState<Record<string, boolean>>({});
  const hasLoadedDamageTakenSettings = useRef(false);

  useEffect(() => {
    // ダメージ結果の追加設定（急所/じゅうでん/可変威力など）は、ポケモン本体の入力値とは別で localStorage に保持する
    const stored = storageClient.pokemon.damageSettingUi.load();
    const damageTaken = stored.success ? stored.data?.damageTaken : undefined;
    if (damageTaken) {
      const selectedMoveKeys = damageTaken.selectedMoveKeys ?? [];
      const loadedMoveSettingByKey = damageTaken.moveSettingByKey ?? {};
      const legacyCommonSettingByOpponentId = (damageTaken as any)?.commonSettingByOpponentId as
        | Record<string, { isJuuden?: boolean } | undefined>
        | undefined;

      setSelectedDamageTakenMoveKeys(selectedMoveKeys);

      if (!legacyCommonSettingByOpponentId) {
        setDamageTakenMoveSettingByKey(loadedMoveSettingByKey);
      } else {
        // 旧スキーマ（相手ごと共通のじゅうでん設定）を技ごとの設定へ移行する
        const next: Record<string, MoveDamageSetting> = { ...loadedMoveSettingByKey };
        const keySet = new Set<string>([...Object.keys(loadedMoveSettingByKey), ...selectedMoveKeys]);
        keySet.forEach((moveKey) => {
          const { opponentId } = parseDamageTakenMoveKey(moveKey);
          const legacyJuuden = legacyCommonSettingByOpponentId?.[opponentId]?.isJuuden;
          if (typeof legacyJuuden !== 'boolean') {
            return;
          }
          const current = next[moveKey] ?? {};
          if (current.isJuuden === undefined) {
            next[moveKey] = { ...current, isJuuden: legacyJuuden };
          }
        });
        setDamageTakenMoveSettingByKey(next);
      }
    }
    hasLoadedDamageTakenSettings.current = true;
  }, []);

  const assumedMoves = useMemo(() => {
    if (!selectedOpponent) return [];
    return getMovesByIds(selectedOpponent.assumedMoveIds ?? []);
  }, [selectedOpponent]);

  // モーダル表示のため、相手カードをIDで引けるようにしておく
  const opponentsById = useMemo(() => {
    const map = new Map<string, OpponentCard>();
    opponents.forEach((opponent) => {
      map.set(opponent.id, opponent);
    });
    return map;
  }, [opponents]);

  // 相手ごとの想定技をまとめて引けるようにしておく
  const assumedMovesByOpponentId = useMemo(() => {
    const map = new Map<string, Move[]>();
    opponents.forEach((opponent) => {
      map.set(opponent.id, getMovesByIds(opponent.assumedMoveIds ?? []));
    });
    return map;
  }, [opponents]);

  const availableOpponentMoves = useMemo(() => {
    if (!selectedOpponentPokemon) return [];
    return getMovesByIds(selectedOpponentPokemon.move_id_list).sort((a, b) => a.name_ja.localeCompare(b.name_ja));
  }, [selectedOpponentPokemon]);

  const defenderHp = useMemo(() => {
    if (!selectedPokemon) return 0;
    return calcRealValueHPStat(selectedPokemon);
  }, [selectedPokemon]);

  const opponentHp = useMemo(() => {
    if (!selectedOpponent?.defenseProfile) {
      return 0;
    }
    const actualStats = buildOpponentActualStats(selectedOpponent.defenseProfile);
    return actualStats?.hp ?? 0;
  }, [selectedOpponent]);

  const selfScarfFromItem = selectedPokemon?.selected_item_id === OFFENCE_ITEM_ID_KODAWARISUKAFU;

  const opponentSpeedProfile = selectedOpponent?.offenseProfile ?? selectedOpponent?.defenseProfile ?? null;
  const commonProfile = selectedOpponent?.defenseProfile ?? selectedOpponent?.offenseProfile ?? null;
  const opponentScarfFromItem = opponentSpeedProfile?.selectedItemId === OFFENCE_ITEM_ID_KODAWARISUKAFU;

  const sectionCollapsed = (key: SectionKey) => uiFlags[key]?.collapsed ?? false;

  const handleToggleSectionCollapsed = (key: SectionKey) => {
    setUiFlags((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] ?? { enabled: true, collapsed: false }),
        collapsed: !(prev[key]?.collapsed ?? false),
      },
    }));
  };

  // 共通設定は防御/攻撃の両方に反映する
  const applyToCommonProfiles = (apply: (profileId: string) => void) => {
    const idSet = new Set<string>();
    if (selectedOpponent?.defenseProfile) {
      idSet.add(selectedOpponent.defenseProfile.id);
    }
    if (selectedOpponent?.offenseProfile) {
      idSet.add(selectedOpponent.offenseProfile.id);
    }
    idSet.forEach((profileId) => apply(profileId));
  };

  const handleUpdateSelfSpeedModifier = useCallback((field: keyof SpeedModifiers, checked: boolean) => {
    updateSelectedSpeedSettings({
      ...selectedSpeedSettings,
      self: {
        ...selectedSpeedSettings.self,
        [field]: checked,
      },
    });
  }, [selectedSpeedSettings, updateSelectedSpeedSettings]);

  const handleUpdateOpponentSpeedModifier = useCallback((field: keyof SpeedModifiers, checked: boolean) => {
    updateSelectedSpeedSettings({
      ...selectedSpeedSettings,
      opponent: {
        ...selectedSpeedSettings.opponent,
        [field]: checked,
      },
    });
  }, [selectedSpeedSettings, updateSelectedSpeedSettings]);

  const handleUpdateTrickRoom = useCallback((checked: boolean) => {
    updateSelectedSpeedSettings({
      ...selectedSpeedSettings,
      isTrickRoom: checked,
    });
  }, [selectedSpeedSettings, updateSelectedSpeedSettings]);

  const handleSelectCommonItem: typeof handleSelectItemForAttackPokemon = (_id, itemId) => {
    applyToCommonProfiles((profileId) => handleSelectItemForAttackPokemon(profileId, itemId));
  };

  const handleSelectCommonAbility: typeof handleSelectAbilityForAttackPokemon = (_id, abilityId) => {
    applyToCommonProfiles((profileId) => handleSelectAbilityForAttackPokemon(profileId, abilityId));
  };

  const handleUpdateCommonKodaiQuark: typeof handleUpdateKodaiQuarkStat = (_id, value) => {
    applyToCommonProfiles((profileId) => handleUpdateKodaiQuarkStat(profileId, value));
  };

  const handleSelectCommonTera: typeof handleSelectTerasuTypeForAttackPokemon = (_id, terasuType) => {
    applyToCommonProfiles((profileId) => handleSelectTerasuTypeForAttackPokemon(profileId, terasuType));
  };

  const handleUpdateCommonStats: typeof handleUpdateAttackPokemonStats = (_id, field, value) => {
    applyToCommonProfiles((profileId) => handleUpdateAttackPokemonStats(profileId, field, value));
  };

  const handleUpdateCommonPersonality: typeof handleUpdateAttackPokemonPersonality = (_id, field, value) => {
    applyToCommonProfiles((profileId) => handleUpdateAttackPokemonPersonality(profileId, field, value));
  };

  const handleUpdateCommonRank: typeof handleUpdateAttackPokemonRank = (_id, field, value) => {
    applyToCommonProfiles((profileId) => handleUpdateAttackPokemonRank(profileId, field, value));
  };

  const handleSelectCommonStatusAilment = (statusAilment: StatusAilmentValue) => {
    applyToCommonProfiles((profileId) => handleUpdateAttackPokemonStatus(
      profileId,
      statusAilment === 'burned',
      statusAilment === 'poisoned',
      statusAilment === 'paralyzed',
    ));
  };

  const renderSectionHeader = (
    title: string,
    key: SectionKey,
    extra?: React.ReactNode,
  ) => {
    const collapsed = sectionCollapsed(key);
    const handleHeaderKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleToggleSectionCollapsed(key);
      }
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box
          role="button"
          tabIndex={0}
          aria-expanded={!collapsed}
          onClick={() => handleToggleSectionCollapsed(key)}
          onKeyDown={handleHeaderKeyDown}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', flex: 1 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {extra && (
            <Box onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
              {extra}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            aria-expanded={!collapsed}
            onClick={() => handleToggleSectionCollapsed(key)}
          >
            {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Box>
      </Box>
    );
  };

  const renderCommonConditionsHeader = () => {
    const collapsed = sectionCollapsed('commonConditions');
    const handleHeaderKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleToggleSectionCollapsed('commonConditions');
      }
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box
          role="button"
          tabIndex={0}
          aria-expanded={!collapsed}
          onClick={() => handleToggleSectionCollapsed('commonConditions')}
          onKeyDown={handleHeaderKeyDown}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', flex: 1 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            能力設定
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            aria-expanded={!collapsed}
            onClick={() => handleToggleSectionCollapsed('commonConditions')}
          >
            {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Box>
      </Box>
    );
  };

  const renderOpponentCard = (opponent: OpponentCard) => {
    const pokemon = resolveOpponentPokemon(opponent);
    if (!pokemon) return null;

    const profile = resolveOpponentProfile(opponent);
    const actualStats = buildOpponentActualStats(profile);
    const opponentSpeedSettings = normalizeOpponentSpeedSettings(opponent.speedSettings);
    const selfSpeedModifiersForCard = opponentSpeedSettings.self;
    const opponentSpeedModifiersForCard = opponentSpeedSettings.opponent;
    const isTrickRoomForCard = opponentSpeedSettings.isTrickRoom;
    const statsLabel = actualStats
      ? `H:${actualStats.hp} C:${actualStats.specialAttack}\nA:${actualStats.attack} D:${actualStats.specialDefense}\nB:${actualStats.defense} S:${actualStats.speed}`
      : null;

    const cardSpeedProfile = opponent.offenseProfile ?? opponent.defenseProfile ?? null;
    const speedResultLabel =
      selectedPokemon && cardSpeedProfile
        ? resolveSpeedResultLabel(
            applySpeedModifiers(
              calculateOffenceSpeed(selectedPokemon),
              selectedPokemon.rankCorrection?.speed ?? 0,
              {
                scarf: Boolean(selfScarfFromItem),
                tailwind: selfSpeedModifiersForCard.tailwind,
                paralysis: selfSpeedModifiersForCard.paralysis,
              },
            ),
            applySpeedModifiers(
              calculateOffenceSpeed(buildSpeedBasePokemon(cardSpeedProfile)),
              resolveSpeedRank(cardSpeedProfile),
              {
                scarf: cardSpeedProfile.selectedItemId === OFFENCE_ITEM_ID_KODAWARISUKAFU,
                tailwind: opponentSpeedModifiersForCard.tailwind,
                paralysis: opponentSpeedModifiersForCard.paralysis,
              },
            ),
            isTrickRoomForCard,
          )
        : null;

    const selectedItemId =
      opponent.defenseProfile?.selectedItemId ?? opponent.offenseProfile?.selectedItemId ?? pokemon.selected_item_id;

    const pokemonTypeIdList = getArceusEffectiveType({
      id: pokemon.id,
      species_id: pokemon.species_id,
      type_id_list: pokemon.type_id_list,
      selected_item_id: selectedItemId,
    });

    const isSelected = opponent.id === selectedOpponentId;
    const speedBackgroundColor =
      speedResultLabel === '先手'
        ? '#c8e6c9'
        : speedResultLabel === '同速'
        ? '#fff3c4'
        : speedResultLabel === '後手'
        ? '#ffcdd2'
        : isSelected
        ? '#e3f2fd'
        : 'white';

    return (
      <AttackPokemonCardFrame
        key={opponent.id}
        pokemonId={pokemon.id}
        pokemonName={pokemon.name_ja}
        pictureUrl={pokemon.picture_url}
        pokemonTypeIdList={pokemonTypeIdList}
        backgroundColor={speedBackgroundColor}
        border={isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0'}
        onClick={() => onSelectOpponent(opponent.id)}
        hoverBackgroundColor={isSelected ? undefined : speedBackgroundColor === 'white' ? '#f5f5f5' : speedBackgroundColor}
        cursor="pointer"
        actions={
          <>
            <IconButton
              size="small"
              onClick={() => onCopyOpponent(opponent.id)}
              sx={{
                color: 'primary.main',
                '&:hover': {
                  color: 'primary.dark',
                  backgroundColor: 'primary.light',
                },
              }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onRemoveOpponent(opponent.id)}
              sx={{
                color: 'error.main',
                '&:hover': {
                  color: 'error.dark',
                  backgroundColor: 'error.light',
                },
              }}
            >
              <CancelIcon fontSize="small" />
            </IconButton>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            {speedResultLabel && (
              <Chip
                label={speedResultLabel}
                size="small"
                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
              />
            )}
          </Box>
          {statsLabel && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Typography variant="caption" color="text.secondary">
                実数値
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                {statsLabel}
              </Typography>
            </Box>
          )}
          {uiFlags.opponentList.showTags && opponent.tags && opponent.tags.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {opponent.tags.map((tag) => (
                <Chip key={tag} label={tag} size="small" />
              ))}
            </Box>
          )}
        </Box>
      </AttackPokemonCardFrame>
    );
  };

  const damageTakenItems = useMemo(() => {
    if (!uiFlags.damageTaken.enabled) {
      return [] as Array<{
        move: Move;
        detail: retCalc | null;
        text: string;
        category: DamageCategory;
      }>;
    }
    const offenseProfile = selectedOpponent?.offenseProfile;
    if (!selectedPokemon || !offenseProfile) {
      return [] as Array<{
        move: Move;
        detail: retCalc | null;
        text: string;
        category: DamageCategory;
      }>;
    }

    return assumedMoves
      .filter((move) => move)
      .map((move) => {
        if (move.damage_class_number === MOVE_DAMAGE_CLASS_STATUS) {
          return {
            move,
            detail: null,
            text: '変化技',
            category: 'invalid' as DamageCategory,
          };
        }

        const moveKey = createDamageTakenMoveKey(selectedOpponent.id, move.id);
        const moveSetting = resolveMoveDamageSettingWithDefaults(move, damageTakenMoveSettingByKey[moveKey]);
        const resolvedJuuden = moveSetting.isJuuden ?? Boolean(offenseProfile.isJuuden);

        // 設定が他の技に漏れないよう、対象フィールドは必ず上書きする
        const attackProfile: AttackPokemonData = {
          ...offenseProfile,
          selectedMoveId: move.id,
          selectedMove: move,
          isJuuden: resolvedJuuden,
          isCritical: moveSetting.isCritical,
          criticalHitCount: moveSetting.criticalHitCount,
          isDoublePower: moveSetting.isDoublePower,
          customPower: moveSetting.customPower,
        };

        const damageText = calculateDamage(attackProfile, selectedPokemon, selectedOpponentOtherSetting);
        const detail = getDamageDetail(attackProfile, selectedPokemon, selectedOpponentOtherSetting);
        const category = getDamageCategory(detail, move, defenderHp);

        return {
          move,
          detail,
          text: damageText,
          category,
        };
      });
  }, [
    assumedMoves,
    calculateDamage,
    damageTakenMoveSettingByKey,
    defenderHp,
    getDamageDetail,
    selectedOpponent,
    selectedOpponentOtherSetting,
    selectedPokemon,
    uiFlags.damageTaken.enabled,
  ]);

  const selectableDamageTakenMoveKeys = useMemo(() => {
    const keys = new Set<string>();
    opponents.forEach((opponent) => {
      if (!opponent.offenseProfile) {
        return;
      }
      const moves = assumedMovesByOpponentId.get(opponent.id) ?? [];
      moves.forEach((move) => {
        if (move && isDamagingMove(move)) {
          keys.add(createDamageTakenMoveKey(opponent.id, move.id));
        }
      });
    });
    return Array.from(keys);
  }, [assumedMovesByOpponentId, opponents]);

  useEffect(() => {
    // 合算対象の技だけが残るように選択状態を掃除する
    setSelectedDamageTakenMoveKeys((prev) => {
      const selectable = new Set(selectableDamageTakenMoveKeys);
      return prev.filter((key) => selectable.has(key));
    });
  }, [selectableDamageTakenMoveKeys]);

  useEffect(() => {
    // 想定技が変わったとき、詳細開閉の状態を掃除する
    setDamageTakenDetailOpenByKey((prev) => {
      const selectable = new Set(selectableDamageTakenMoveKeys);
      const next: Record<string, boolean> = {};
      Object.entries(prev).forEach(([key, isOpen]) => {
        if (isOpen && selectable.has(key)) {
          next[key] = isOpen;
        }
      });
      return next;
    });
  }, [selectableDamageTakenMoveKeys]);

  useEffect(() => {
    // 想定技が変わったとき、存在しないキーの追加設定を掃除する
    setDamageTakenMoveSettingByKey((prev) => {
      const selectable = new Set(selectableDamageTakenMoveKeys);
      const next: Record<string, MoveDamageSetting> = {};
      Object.entries(prev).forEach(([key, setting]) => {
        if (selectable.has(key)) {
          next[key] = setting;
        }
      });
      return next;
    });
  }, [selectableDamageTakenMoveKeys]);

  const handleToggleDamageTakenMove = useCallback((moveKey: string) => {
    setSelectedDamageTakenMoveKeys((prev) =>
      prev.includes(moveKey) ? prev.filter((key) => key !== moveKey) : [...prev, moveKey],
    );
  }, []);

  const handleToggleDamageTakenDetail = useCallback((moveKey: string) => {
    setDamageTakenDetailOpenByKey((prev) => ({
      ...prev,
      [moveKey]: !prev[moveKey],
    }));
  }, []);

  const handleUpdateDamageTakenMoveSetting = useCallback((moveKey: string, nextSetting: MoveDamageSetting) => {
    setDamageTakenMoveSettingByKey((prev) => ({
      ...prev,
      [moveKey]: nextSetting,
    }));
  }, []);

  useEffect(() => {
    if (!hasLoadedDamageTakenSettings.current) {
      return;
    }
    const result = storageClient.pokemon.damageSettingUi.update((prev) => ({
      ...prev,
      damageTaken: {
        selectedMoveKeys: selectedDamageTakenMoveKeys,
        moveSettingByKey: damageTakenMoveSettingByKey,
      },
    }));
    if (!result.success) {
      console.error('追加設定UIの保存に失敗しました:', result.error);
    }
  }, [damageTakenMoveSettingByKey, selectedDamageTakenMoveKeys]);

  // 相手横断で選択した技を再計算し、合算モーダルに表示する
  const damageTakenSummaryItems = useMemo(() => {
    if (!selectedPokemon || selectedDamageTakenMoveKeys.length === 0) {
      return [];
    }
    const items: Array<{
      key: string;
      opponent: OpponentCard;
      move: Move;
      detail: retCalc;
      text: string;
      category: DamageCategory;
    }> = [];

    selectedDamageTakenMoveKeys.forEach((key) => {
      const { opponentId, moveId } = parseDamageTakenMoveKey(key);
      const opponent = opponentsById.get(opponentId);
      if (!opponent?.offenseProfile) {
        return;
      }
      const moves = assumedMovesByOpponentId.get(opponentId) ?? [];
      const move = moves.find((m) => m.id === moveId);
      if (!move || !isDamagingMove(move)) {
        return;
      }
      const moveSetting = resolveMoveDamageSettingWithDefaults(move, damageTakenMoveSettingByKey[key]);
      const resolvedJuuden = moveSetting.isJuuden ?? Boolean(opponent.offenseProfile.isJuuden);

      // 設定が他の技に漏れないよう、対象フィールドは必ず上書きする
      const attackProfile: AttackPokemonData = {
        ...opponent.offenseProfile,
        selectedMoveId: move.id,
        selectedMove: move,
        isJuuden: resolvedJuuden,
        isCritical: moveSetting.isCritical,
        criticalHitCount: moveSetting.criticalHitCount,
        isDoublePower: moveSetting.isDoublePower,
        customPower: moveSetting.customPower,
      };
      const opponentSetting = opponent.otherSetting ?? currentOtherSetting;
      const text = calculateDamage(attackProfile, selectedPokemon, opponentSetting);
      const detail = getDamageDetail(attackProfile, selectedPokemon, opponentSetting);
      const category = getDamageCategory(detail, move, defenderHp);
      items.push({
        key,
        opponent,
        move,
        detail,
        text,
        category,
      });
    });

    return items;
  }, [
    assumedMovesByOpponentId,
    calculateDamage,
    damageTakenMoveSettingByKey,
    currentOtherSetting,
    defenderHp,
    getDamageDetail,
    opponentsById,
    selectedDamageTakenMoveKeys,
    selectedPokemon,
  ]);

  const calculateAttackModeDamageForComprehensive = useCallback(
    (
      attackerPokemon: PokemonV2,
      targetPokemon: AttackPokemonData,
      _overrideOtherSetting?: otherSetting,
      resolveMoveSetting?: (moveId: number) => MoveDamageSetting | undefined,
    ) =>
      calculateAttackModeDamage(
        attackerPokemon,
        targetPokemon,
        selectedOpponentOtherSetting,
        resolveMoveSetting,
      ),
    [calculateAttackModeDamage, selectedOpponentOtherSetting],
  );

  const getAttackModeDamageDetailForComprehensive = useCallback(
    (
      attackerPokemon: PokemonV2,
      targetPokemon: AttackPokemonData,
      _overrideOtherSetting?: otherSetting,
      resolveMoveSetting?: (moveId: number) => MoveDamageSetting | undefined,
    ) =>
      getAttackModeDamageDetail(
        attackerPokemon,
        targetPokemon,
        selectedOpponentOtherSetting,
        resolveMoveSetting,
      ),
    [getAttackModeDamageDetail, selectedOpponentOtherSetting],
  );

  const renderDamageTakenList = () => {
    if (!selectedPokemon) {
      return (
        <Typography variant="body2" color="text.secondary">
          自分ポケモンを選択してください
        </Typography>
      );
    }
    if (!selectedOpponent?.offenseProfile) {
      return (
        <Typography variant="body2" color="text.secondary">
          被ダメ設定がありません
        </Typography>
      );
    }

    if (assumedMoves.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          想定技が未設定です
        </Typography>
      );
    }

    const resolveProfileTeraType = (profile: AttackPokemonData): PokemonType | undefined => {
      if (profile.terasuType && profile.terasuType > 0) {
        return profile.terasuType as PokemonType;
      }
      if (profile.terasuState === 'type') {
        return profile.pokemon.type_id_list?.[0] as PokemonType | undefined;
      }
      if (profile.terasuState === 'stellar') {
        return 19 as PokemonType;
      }
      return undefined;
    };

    const offencePokemonForTypeResolve: PokemonV2 = {
      ...selectedOpponent.offenseProfile.pokemon,
      selected_item_id: selectedOpponent.offenseProfile.selectedItemId,
      selected_ability_id: selectedOpponent.offenseProfile.selectedAbilityId,
      selected_ability: selectedOpponent.offenseProfile.selectedAbility,
      terasu_type: resolveProfileTeraType(selectedOpponent.offenseProfile),
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {damageTakenItems.map((item) => {
          const isSelectable = Boolean(item.detail) && isDamagingMove(item.move);
          const moveKey = selectedOpponent ? createDamageTakenMoveKey(selectedOpponent.id, item.move.id) : '';
          const isChecked = moveKey ? selectedDamageTakenMoveKeys.includes(moveKey) : false;
          const moveSetting = moveKey ? damageTakenMoveSettingByKey[moveKey] ?? {} : {};
          const showJuuden =
            resolveOffenceMoveType(item.move, offencePokemonForTypeResolve, selectedOpponentOtherSetting, selectedPokemon) ===
            POKEMON_TYPE_ELECTRIC;
          const summaryTags = buildDamageSettingSummaryTags(item.move, moveSetting, showJuuden);
          if (isChecked) {
            summaryTags.push({ key: `sum-${moveKey}`, label: '合算' });
          }
          const { hasAnyControl } = resolveDamageSettingVisibility(item.move, showJuuden);
          const isDetailOpen = moveKey ? Boolean(damageTakenDetailOpenByKey[moveKey]) : false;

          return (
            <Paper
              key={item.move.id}
              sx={{
                p: 1.5,
                border: '1px solid #e0e0e0',
                backgroundColor: item.category === 'guaranteed'
                  ? '#ffcdd2'
                  : item.category === 'random'
                  ? '#fff3c4'
                  : item.category === 'survive'
                  ? '#c8e6c9'
                  : '#f5f5f5',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {!isDamagingMove(item.move) && (
                    <Chip label="変化技" size="small" variant="outlined" />
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={isChecked}
                        disabled={!isSelectable}
                        onChange={() => moveKey && handleToggleDamageTakenMove(moveKey)}
                      />
                    }
                    label="合算"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.7rem' } }}
                  />
                </Box>
              </Box>
              {isDamagingMove(item.move) ? (
                <Box sx={{ minWidth: 0 }}>
                  <DamageResultDetails
                    move={item.move}
                    pokemonId={selectedOpponent.offenseProfile?.pokemon.id ?? 0}
                    damageText={item.text}
                    damageDetail={item.detail ?? { text: '', min_damage: 0, max_damage: 0, random_table: {} }}
                    getMoveDisplayName={getMoveDisplayName}
                    getMoveDisplayPower={getMoveDisplayPower}
                  />
                  {summaryTags.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {summaryTags.map((tag) => (
                        <Chip key={tag.key} label={tag.label} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                      ))}
                    </Box>
                  )}
                  {hasAnyControl && moveKey && (
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => handleToggleDamageTakenDetail(moveKey)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleToggleDamageTakenDetail(moveKey);
                        }
                      }}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, cursor: 'pointer', width: 'fit-content' }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        詳細設定
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleToggleDamageTakenDetail(moveKey);
                        }}
                        sx={{ padding: '2px' }}
                      >
                        {isDetailOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Box>
                  )}
                  <Collapse in={isDetailOpen} unmountOnExit>
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                      <DamageSettingHeaderControls
                        move={item.move}
                        setting={moveSetting}
                        onChangeSetting={(next) => moveKey && handleUpdateDamageTakenMoveSetting(moveKey, next)}
                        showJuuden={showJuuden}
                      />
                      <DamageSettingPowerSelect
                        move={item.move}
                        setting={moveSetting}
                        onChangeSetting={(next) => moveKey && handleUpdateDamageTakenMoveSetting(moveKey, next)}
                      />
                    </Box>
                  </Collapse>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {getMoveDisplayName(item.move, selectedOpponent.offenseProfile?.pokemon.id ?? 0)}
                </Typography>
              )}
            </Paper>
          );
        })}
      </Box>
    );
  };

  const renderCommonProfileEditor = (profile: AttackPokemonData) => {
    const selectedStatusAilment: StatusAilmentValue = profile.isBurned
      ? 'burned'
      : profile.isPoisoned
      ? 'poisoned'
      : profile.isParalyzed
      ? 'paralyzed'
      : 'none';

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <AttackPokemonItemInput
            attackPokemon={profile}
            handleSelectItemForAttackPokemon={handleSelectCommonItem}
            getOffenseItems={getOffenseItems}
          />
          <AttackPokemonAbilityInput
            attackPokemon={profile}
            handleSelectAbilityForAttackPokemon={handleSelectCommonAbility}
            handleUpdateKodaiQuarkStat={handleUpdateCommonKodaiQuark}
          />
          <StatusAilmentSelect
            value={selectedStatusAilment}
            onChange={handleSelectCommonStatusAilment}
            width="120px"
            stopPropagation
          />
          <AttackPokemonTeraSelector
            variant="simple"
            attackPokemon={profile}
            handleSelectTerasuTypeForAttackPokemon={handleSelectCommonTera}
          />
        </Box>
        <DefencePokemonStatInputAttackMode
          attackPokemon={profile}
          handleUpdateAttackPokemonStats={handleUpdateCommonStats}
          handleUpdateAttackPokemonPersonality={handleUpdateCommonPersonality}
          handleUpdateAttackPokemonRank={handleUpdateCommonRank}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: 2,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', lg: '38%' },
            minWidth: { lg: 340 },
            maxWidth: { lg: 520 },
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            minHeight: 0,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                相手カード一覧
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setIsDamageTakenSummaryOpen(true)}
                disabled={damageTakenSummaryItems.length === 0 || defenderHp <= 0}
              >
                合算結果を表示
              </Button>
            </Box>
            <PokemonSelectInput
              pokemonOptions={pokemonOptions}
              handleAddAttackPokemon={onAddOpponent}
              label="相手ポケモンを追加"
              placeholder="相手ポケモンを選択して追加"
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              overflowY: 'scroll',
              scrollbarGutter: 'stable',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
              pr: 0.5,
              pt: 1,
              borderTop: '1px solid #e0e0e0',
            }}
          >
            {opponents.length > 0 ? (
              opponents.map(renderOpponentCard)
            ) : (
              <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  相手カードがありません
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: 'scroll',
              scrollbarGutter: 'stable',
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              pr: 1,
              pt: 1,
              borderTop: '1px solid #e0e0e0',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                pb: 1.5,
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  計算結果
                </Typography>
              </Box>

              {!selectedOpponentPokemon ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  相手カードを選択してください
                </Typography>
              ) : null}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {renderCommonConditionsHeader()}
                {sectionCollapsed('commonConditions') ? (
                  <></>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        環境設定
                      </Typography>
                      <OtherSetting
                        otherSetting={selectedOpponentOtherSetting}
                        handleSaveOtherSetting={handleSaveOtherSetting}
                        canSelectOoame={canSelectOoame}
                        canSelectOohidery={canSelectOohidery}
                        canSelectRankiryuu={canSelectRankiryuu}
                      />
                    </Box>
                    {selectedOpponent && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          能力設定
                        </Typography>
                        {commonProfile ? (
                          renderCommonProfileEditor(commonProfile)
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            共通設定を行うには相手の設定を作成してください
                          </Typography>
                        )}
                        {(!selectedOpponent.defenseProfile || !selectedOpponent.offenseProfile) && (
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {!selectedOpponent.defenseProfile && (
                              <Chip
                                label="防御設定を作成"
                                size="small"
                                color="primary"
                                onClick={() => onCreateDefenseProfile(selectedOpponent.id)}
                              />
                            )}
                            {!selectedOpponent.offenseProfile && (
                              <Chip
                                label="攻撃設定を作成"
                                size="small"
                                color="primary"
                                onClick={() => onCreateOffenseProfile(selectedOpponent.id)}
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Box>

            <Box id={SECTION_IDS.damageDealt} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {renderSectionHeader('相手に与えるダメージ', 'damageDealt')}
              <Divider />
              {sectionCollapsed('damageDealt') ? null : selectedPokemon && selectedOpponent?.defenseProfile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 280 }}>
                    <AttackModeDisplay
                      leftSidePokemon={selectedPokemon}
                      rightSideTargetPokemon={selectedOpponent.defenseProfile}
                      otherSetting={selectedOpponentOtherSetting}
                      calculateAttackModeDamage={calculateAttackModeDamageForComprehensive}
                      getAttackModeDamageDetail={getAttackModeDamageDetailForComprehensive}
                      getMoveDisplayName={getMoveDisplayName}
                      getMoveDisplayPower={getMoveDisplayPower}
                      enableSum
                      defenderHp={opponentHp}
                    />
                  </Box>
                </Box>
              ) : selectedOpponent ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    防御側の設定がありません
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  相手カードを選択してください
                </Typography>
              )}
            </Box>

            <Box id={SECTION_IDS.damageTaken} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {renderSectionHeader('相手から受けるダメージ', 'damageTaken')}
              <Divider />
              {sectionCollapsed('damageTaken') ? null : selectedOpponent && selectedOpponentPokemon ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <InputMoves
                    selectedPokemon={selectedOpponentPokemon}
                    selectedMoves={assumedMoves}
                    availableMoves={availableOpponentMoves}
                    onMoveSelect={(index, move) => onUpdateAssumedMove(selectedOpponent.id, index, move)}
                    getMoveDisplayName={getMoveDisplayName}
                    getMoveDisplayPower={getMoveDisplayPower}
                  />
                  {renderDamageTakenList()}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  相手カードを選択してください
                </Typography>
              )}
            </Box>

            <Box id={SECTION_IDS.speed} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {renderSectionHeader('素早さ', 'speed')}
              <Divider />
              {sectionCollapsed('speed') ? null : selectedPokemon && opponentSpeedProfile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        自分側補正
                      </Typography>
                      <FormControlLabel
                        label="スカーフ"
                        control={
                          <Switch
                            checked={Boolean(selfScarfFromItem)}
                            disabled
                          />
                        }
                      />
                      <FormControlLabel
                        label="追い風"
                        control={
                          <Switch
                            checked={selfSpeedModifiers.tailwind}
                            onChange={(event) => handleUpdateSelfSpeedModifier('tailwind', event.target.checked)}
                          />
                        }
                      />
                      <FormControlLabel
                        label="麻痺"
                        control={
                          <Switch
                            checked={selfSpeedModifiers.paralysis}
                            onChange={(event) => handleUpdateSelfSpeedModifier('paralysis', event.target.checked)}
                          />
                        }
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        相手側補正
                      </Typography>
                      <FormControlLabel
                        label="スカーフ"
                        control={
                          <Switch
                            checked={Boolean(opponentScarfFromItem)}
                            disabled
                          />
                        }
                      />
                      <FormControlLabel
                        label="追い風"
                        control={
                          <Switch
                            checked={opponentSpeedModifiers.tailwind}
                            onChange={(event) => handleUpdateOpponentSpeedModifier('tailwind', event.target.checked)}
                          />
                        }
                      />
                      <FormControlLabel
                        label="麻痺"
                        control={
                          <Switch
                            checked={opponentSpeedModifiers.paralysis}
                            onChange={(event) => handleUpdateOpponentSpeedModifier('paralysis', event.target.checked)}
                          />
                        }
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        ルール
                      </Typography>
                      <FormControlLabel
                        label="トリル"
                        control={<Switch checked={isTrickRoom} onChange={(e) => handleUpdateTrickRoom(e.target.checked)} />}
                      />
                    </Box>
                  </Box>
                  <></>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  相手カードを選択してください
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Modal
        open={isDamageTakenSummaryOpen}
        onClose={() => setIsDamageTakenSummaryOpen(false)}
        aria-labelledby="damage-taken-summary-modal"
        aria-describedby="damage-taken-summary-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(900px, 95vw)',
            maxHeight: '90vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography id="damage-taken-summary-modal" variant="h6" sx={{ fontWeight: 700 }}>
              相手から受けるダメージ 合算結果
            </Typography>
            <Button size="small" variant="outlined" onClick={() => setIsDamageTakenSummaryOpen(false)}>
              閉じる
            </Button>
          </Box>
          {damageTakenSummaryItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              合算対象の技がありません
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {damageTakenSummaryItems.map((item) => (
                <Paper
                  key={item.key}
                  sx={{
                    p: 1.5,
                    border: '1px solid #e0e0e0',
                    backgroundColor: item.category === 'guaranteed'
                      ? '#ffcdd2'
                      : item.category === 'random'
                      ? '#fff3c4'
                      : item.category === 'survive'
                      ? '#c8e6c9'
                      : '#f5f5f5',
                  }}
                >
                  <DamageResultDetails
                    move={item.move}
                    pokemonId={item.opponent.offenseProfile?.pokemon.id ?? 0}
                    damageText={item.text}
                    damageDetail={item.detail}
                    getMoveDisplayName={getMoveDisplayName}
                    getMoveDisplayPower={getMoveDisplayPower}
                    customLabel={`${item.opponent.name} / ${getMoveDisplayName(item.move, item.opponent.offenseProfile?.pokemon.id ?? 0)}`}
                  />
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
}

export default ComprehensiveMode;
